<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import AnimatedLogoLoader from '@/components/createdUi/AnimatedLogoLoader.vue'
import type { IFilterChoiceItem } from '@/modules/universalStats/types/IFilterChoiceItem'
import type { IFilterStore } from '@/types/IFilterStore'

const props = defineProps<{
  fieldKey: string
  store: IFilterStore
  modelValue: string[]
  placeholder?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void
  (e: 'change', value: string[]): void
}>()

const search = ref('')
const open = ref<string[]>([])
const observerElement = ref<HTMLElement | null>(null)
let observer: IntersectionObserver | null = null
let debounceTimer: ReturnType<typeof setTimeout> | null = null

const displayItems = computed<IFilterChoiceItem[]>(() => {
  const rawItems = search.value
    ? (props.store.searchChoices[props.fieldKey] || [])
    : (props.store.filterChoices[props.fieldKey] || [])

  return rawItems || []
})

const highlightMatch = (label: any, searchTerm: string): string => {
  const str = String(label ?? '')
  if (!searchTerm) return str
  const safe = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const re = new RegExp(`(${safe})`, 'gi')
  return str.replace(re, '<mark class="search-highlight">$1</mark>')
}

const loadNextPage = async () => {
  const cursor = props.store.filterCursors[props.fieldKey]
  const isLoading = props.store.filterLoading[props.fieldKey]

  if (cursor && !isLoading) {
    let cursorParam: string | null
    try {
      const url = new URL(cursor)
      cursorParam = url.searchParams.get('cursor')
    } catch {
      cursorParam = cursor
    }

    await props.store.fetchFilterChoices(
      props.fieldKey,
      search.value,
      cursorParam || undefined,
    )
  }
}

// Серверный поиск с дебаунсом 400мс
watch(search, val => {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(async () => {
    await props.store.fetchFilterChoices(props.fieldKey, val || '')
  }, 400)
})

onMounted(() => {
  if (!props.store.filterChoices[props.fieldKey]?.length && !props.store.filterLoading[props.fieldKey]) {
    props.store.fetchFilterChoices(props.fieldKey)
  }

  observer = new IntersectionObserver(async entries => {
    if (entries[0].isIntersecting) {
      await loadNextPage()
    }
  }, {
    threshold: 0.1,
  })

  if (observerElement.value) observer.observe(observerElement.value)
})

watch(
  () => props.fieldKey,
  key => {
    if (key && !props.store.filterChoices[key]?.length && !props.store.filterLoading[key]) {
      props.store.fetchFilterChoices(key)
    }
  },
)

onUnmounted(() => {
  if (observer) observer.disconnect()
  if (debounceTimer) clearTimeout(debounceTimer)
})

const isItemSelected = (item: IFilterChoiceItem): boolean => {
  const val = item.id
  return props.modelValue?.includes(val as any)
    || props.modelValue?.includes(String(val) as any)
    || props.modelValue?.includes(Number(val) as any)
}

const getItemValue = (item: IFilterChoiceItem): string | number => item.id

const getVisibleNodes = (
  nodes: IFilterChoiceItem[],
  expandedKeys: string[],
  depth = 0,
  expandChildren = false,
): any[] => {
  const visible: any[] = []

  nodes.forEach(node => {
    const isLeaf = !node.children || node.children.length === 0
    visible.push({
      ...node,
      depth,
      isLeaf,
    })

    const keyStr = String(getItemValue(node))
    const shouldShowChildren = expandedKeys.includes(keyStr) || expandChildren

    if (shouldShowChildren && node.children?.length) {
      visible.push(...getVisibleNodes(node.children, expandedKeys, depth + 1, expandChildren))
    }
  })

  return visible
}

const visibleNodes = computed(() => {
  const expandChildren = search.value.trim().length > 0
  return getVisibleNodes(displayItems.value, open.value, 0, expandChildren)
})

const toggleNode = (id: string | number) => {
  const idStr = String(id)
  const idx = open.value.indexOf(idStr)
  if (idx === -1) {
    open.value = [...open.value, idStr]
  } else {
    open.value = open.value.filter(v => v !== idStr)
  }
}

const autoExpandSelectedParents = () => {
  if (!props.modelValue?.length || !displayItems.value.length) return
  const selectedSet = new Set(props.modelValue.map(v => String(v)))
  const toOpen = new Set(open.value)

  const checkAndExpand = (node: IFilterChoiceItem): boolean => {
    let hasSelected = selectedSet.has(String(node.id))
    if (node.children?.length) {
      for (const child of node.children) {
        if (checkAndExpand(child)) {
          hasSelected = true
        }
      }
      if (hasSelected) {
        toOpen.add(String(node.id))
      }
    }
    return hasSelected
  }

  displayItems.value.forEach(node => checkAndExpand(node))
  open.value = Array.from(toOpen)
}

watch(
  [() => props.modelValue, () => displayItems.value],
  () => {
    autoExpandSelectedParents()
  },
  { immediate: true, deep: true },
)

const getAllDescendantValues = (node: IFilterChoiceItem): any[] => {
  const vals: any[] = []
  const collect = (n: IFilterChoiceItem) => {
    if (n.children) {
      for (const child of n.children) {
        vals.push(child.id)
        collect(child)
      }
    }
  }
  collect(node)
  return vals
}

const getLeafValues = (node: IFilterChoiceItem): any[] => {
  if (!node.children?.length) {
    return [node.id]
  }
  return node.children.flatMap(child => getLeafValues(child))
}

const isNodeSelected = (node: IFilterChoiceItem): boolean => {
  const strategy = props.store.filterStrategies?.[props.fieldKey] || 'independent'

  if (strategy === 'leaf') {
    if (!node.children?.length) {
      return isItemSelected(node)
    }
    const leaves = getLeafValues(node)
    return leaves.every(val =>
      props.modelValue?.includes(val as any)
      || props.modelValue?.includes(String(val) as any)
      || props.modelValue?.includes(Number(val) as any),
    )
  }
  return isItemSelected(node)
}

const toggleItemSelect = (item: IFilterChoiceItem) => {
  const itemValue = item.id
  const isLeaf = !item.children?.length
  const current = new Set<any>(props.modelValue || [])

  const hasSelected = (val: any) => {
    return current.has(val) || current.has(String(val)) || current.has(Number(val))
  }

  const addSelected = (val: any) => {
    current.add(val)
  }

  const deleteSelected = (val: any) => {
    current.delete(val)
    current.delete(String(val))
    current.delete(Number(val))
  }

  const strategy = props.store.filterStrategies?.[props.fieldKey] || 'independent'

  if (strategy === 'trunk') {
    const descendantValues = getAllDescendantValues(item)
    const allSelected = descendantValues.every(val => hasSelected(val)) && hasSelected(itemValue)

    if (allSelected) {
      deleteSelected(itemValue)
      descendantValues.forEach(val => deleteSelected(val))
    } else {
      addSelected(itemValue)
      descendantValues.forEach(val => addSelected(val))

      if (!isLeaf) {
        const toExpand = [itemValue, ...descendantValues]
        const newOpen = new Set(open.value)
        toExpand.forEach(id => newOpen.add(String(id)))
        open.value = Array.from(newOpen)
      }
    }
  } else if (strategy === 'leaf') {
    const leafValues = getLeafValues(item)
    const allSelected = leafValues.every(val => hasSelected(val))

    if (allSelected) {
      leafValues.forEach(val => deleteSelected(val))
    } else {
      leafValues.forEach(val => addSelected(val))

      if (!isLeaf) {
        const collectSubGroupIds = (node: IFilterChoiceItem): any[] => {
          if (!node.children?.length) return []
          return [node.id, ...node.children.flatMap(child => collectSubGroupIds(child))]
        }
        const toExpand = collectSubGroupIds(item)
        const newOpen = new Set(open.value)
        toExpand.forEach(id => newOpen.add(String(id)))
        open.value = Array.from(newOpen)
      }
    }
  } else {
    if (hasSelected(itemValue)) {
      deleteSelected(itemValue)
    } else {
      addSelected(itemValue)
    }
  }

  const updated = Array.from(current).map(v => String(v))
  emit('update:modelValue', updated)
  emit('change', updated)
}

const isComparisonActive = computed(() => {
  return (props.store as any)?.groupBy === props.fieldKey
})

const onToggleCompare = async (val: boolean | null) => {
  if (props.store && 'setGroupBy' in props.store) {
    const target = (props.store as any).currentTarget
    if (val) {
      await (props.store as any).setGroupBy(target, props.fieldKey)
    } else {
      await (props.store as any).setGroupBy(target, null)
    }
  }
}
</script>

<template>
  <div class="dashboard-filter-tree">
    <!-- Поле поиска и переключатель сравнения -->
    <div class="filter-search-wrapper px-3 pt-2 pb-1">
      <div class="compare-switch-wrapper d-flex align-center justify-space-between mb-2 px-1">
        <span
          class="text-caption font-weight-medium"
          :class="modelValue && modelValue.length >= 2 ? 'text-high-emphasis' : 'text-disabled'"
        >
          Сравнить на графике
        </span>
        <VSwitch
          :model-value="isComparisonActive"
          density="compact"
          hide-details
          color="primary"
          :disabled="!modelValue || modelValue.length < 2"
          @update:model-value="onToggleCompare"
        />
      </div>

      <VTextField
        v-model="search"
        :placeholder="placeholder || 'Поиск...'"
        variant="outlined"
        density="compact"
        :append-inner-icon="search ? undefined : 'ri-search-line'"
        hide-details
        clearable
        class="search-field mb-2"
        @click:clear="search = ''"
      />
    </div>

    <!-- Список элементов дерева в стиле UniversalFilterTree -->
    <div class="v-list custom-treeview px-2">
      <div
        v-for="item in visibleNodes"
        :key="getItemValue(item)"
        class="v-list-item filter-tree-node d-flex align-center py-1 px-1 rounded cursor-pointer"
        :class="{
          'v-list-item--active': isNodeSelected(item),
          'tree-item--selected': isNodeSelected(item),
          'tree-item--leaf': item.isLeaf,
        }"
        :style="{
          '--indent-level': item.depth,
          'padding-inline-start': `calc(${item.depth} * 24px + 6px) !important`,
        }"
        @click.stop="toggleItemSelect(item)"
      >
        <!-- Кнопка раскрытия для узлов с детьми ИЛИ фиксированный спейсер для листьев -->
        <div class="v-list-item-action v-list-item-action--start me-1 d-flex align-center justify-center flex-shrink-0">
          <VIconBtn
            v-if="!item.isLeaf"
            icon="ri-play-fill"
            class="expand-btn"
            variant="text"
            :class="{ expanded: open.includes(String(getItemValue(item))) }"
            @click.stop="toggleNode(getItemValue(item))"
          />
          <div v-else class="node-leaf-spacer" />
        </div>

        <!-- Чекбокс выбора -->
        <div class="node-checkbox-wrapper me-2" @click.stop="toggleItemSelect(item)">
          <VCheckboxBtn
            :model-value="isNodeSelected(item)"
            density="compact"
            color="primary"
            class="node-checkbox"
          />
        </div>

        <!-- Название элемента -->
        <div class="v-list-item__content flex-grow-1 min-width-0">
          <div class="v-list-item-title">
            <div
              class="text-body-1 text-high-emphasis py-1 text-wrap w-100 cursor-pointer"
              v-html="highlightMatch(item.label, search) /* eslint-disable-line vue/no-v-html */"
            />
          </div>
        </div>

        <!-- Количество записей (count) -->
        <div v-if="item.count !== undefined" class="node-count ms-2 text-caption text-medium-emphasis">
          {{ item.count }}
        </div>
      </div>

      <!-- Лоадер или сообщение об окончании данных -->
      <div
        ref="observerElement"
        class="d-flex justify-center align-center py-2"
        style="min-height: 32px;"
      >
        <AnimatedLogoLoader
          v-if="store.filterLoading[fieldKey]"
          :size="20"
        />
        <span
          v-else-if="!store.filterCursors[fieldKey] && displayItems.length > 15"
          class="text-caption text-disabled"
        >
          Все данные загружены
        </span>
      </div>

      <div
        v-if="displayItems.length === 0 && !store.filterLoading[fieldKey]"
        class="py-3 text-center text-caption text-disabled"
      >
        Ничего не найдено
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.dashboard-filter-tree {
  width: 100%;
}

.search-field {
  :deep(.v-field) {
    border-radius: 8px;
  }
}

.custom-treeview {
  background: transparent !important;

  .v-list-item {
    min-height: 36px;
    padding-top: 2px;
    padding-bottom: 2px;
    padding-right: 8px;
    border-radius: 6px;
    margin-bottom: 2px;
    cursor: pointer;
    user-select: none;
    transition: background-color 0.12s ease;

    &:hover {
      background-color: rgba(var(--v-theme-on-surface), 0.04);
    }

    &.v-list-item--active,
    &.tree-item--selected {
      background-color: rgba(var(--v-theme-primary), 0.08);

      :deep(.v-list-item-title),
      :deep(.v-list-item-title div) {
        color: rgb(var(--v-theme-primary)) !important;
        font-weight: 500 !important;
      }
    }
  }

  :deep(.v-list-item-title),
  :deep(.v-list-item-title div) {
    font-size: 14px !important;
    font-weight: 400 !important;
    line-height: 20px !important;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity)) !important;
  }


  .node-checkbox-wrapper {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  .node-checkbox {
    :deep(.v-selection-control) {
      min-height: auto;
    }
  }

  .node-count {
    font-size: 12px;
    font-weight: 500;
    flex-shrink: 0;
  }
}

/* Стили для подсветки найденных совпадений */
:deep(.search-highlight) {
  background-color: rgb(var(--v-theme-primary-500), var(--v-activated-opacity));
  padding: 2px 0;
  border-radius: 2px;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
}

:deep(.v-theme--dark .search-highlight) {
  background-color: rgba(var(--v-theme-primary-500), 0.38);
  padding: 2px 0;
  border-radius: 2px;
  color: #fff;
}
</style>
